import { useCallback } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";

type DirtyFields<T> = {
	[K in keyof T]?: boolean | DirtyFields<T[K]>;
};

function extractDirtyValues<T extends FieldValues>(
	dirtyFields: DirtyFields<T>,
	allValues: T,
): Partial<T> {
	const dirtyValues: Partial<T> = {};

	if (allValues == null || typeof dirtyFields !== "object") return dirtyValues;

	for (const key of Object.keys(dirtyFields) as Array<keyof T>) {
		const isDirty = dirtyFields[key];
		const value = allValues[key];

		if (value === undefined) continue;

		if (isDirty === true) {
			dirtyValues[key] = value;
			continue;
		}

		// Arrays are treated atomically: per-index dirty maps can desync from the
		// current values after useFieldArray.remove.
		if (Array.isArray(isDirty)) {
			dirtyValues[key] = value;
			continue;
		}

		if (typeof isDirty === "object" && isDirty !== null && value !== null) {
			const nestedDirty = extractDirtyValues(
				isDirty as DirtyFields<T[keyof T]>,
				value as T[keyof T],
			);
			if (Object.keys(nestedDirty).length > 0) {
				dirtyValues[key] = nestedDirty as T[keyof T];
			}
		}
	}

	return dirtyValues;
}

export function useDirtyValues<T extends FieldValues>(
	form: Pick<UseFormReturn<T>, "formState" | "getValues">,
) {
	const { formState, getValues } = form;

	const getDirtyValues = useCallback((): Partial<T> => {
		const allValues = getValues();
		return extractDirtyValues(
			formState.dirtyFields as DirtyFields<T>,
			allValues,
		);
	}, [formState.dirtyFields, getValues]);

	return getDirtyValues;
}

export { extractDirtyValues };
